/**
 * Sound Notification Hook
 * 
 * Plays notification sounds for admin alerts
 * Supports different sound types for different events
 */

import { useCallback, useRef, useEffect } from 'react';

// Notification sound URLs (using free sounds or base64)
const NOTIFICATION_SOUNDS = {
  // Ding sound for general notifications
  default: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleU09teleU09teleU09teleU0=',
  
  // Alert sound for urgent live chat requests
  urgent: null, // Will be created with Web Audio API
  
  // Gentle notification for regular updates
  gentle: null
};

// Create an urgent alert sound using Web Audio API
const createUrgentAlertSound = (audioContext) => {
  const duration = 0.5;
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
  oscillator.frequency.setValueAtTime(660, audioContext.currentTime + 0.1); // E5
  oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.2); // A5
  
  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
};

// Create a gentle notification sound
const createGentleSound = (audioContext) => {
  const duration = 0.3;
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
  oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
  
  gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
};

/**
 * Hook to play notification sounds
 * @param {boolean} enabled - Whether sounds are enabled
 * @returns {Object} - Methods to play different notification sounds
 */
export const useSoundNotification = (enabled = true) => {
  const audioContextRef = useRef(null);
  const isEnabledRef = useRef(enabled);
  
  // Update enabled ref when prop changes
  useEffect(() => {
    isEnabledRef.current = enabled;
  }, [enabled]);
  
  // Initialize audio context on first user interaction
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        console.warn('Web Audio API not supported:', e);
      }
    }
    return audioContextRef.current;
  }, []);
  
  // Play the urgent alert sound (for live chat requests)
  const playUrgentAlert = useCallback(() => {
    if (!isEnabledRef.current) return;
    
    const audioContext = initAudioContext();
    if (audioContext) {
      // Resume audio context if suspended (browser policy)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      createUrgentAlertSound(audioContext);
      
      // Play it twice for emphasis
      setTimeout(() => {
        if (audioContext.state === 'running') {
          createUrgentAlertSound(audioContext);
        }
      }, 600);
    }
  }, [initAudioContext]);
  
  // Play gentle notification sound
  const playGentleNotification = useCallback(() => {
    if (!isEnabledRef.current) return;
    
    const audioContext = initAudioContext();
    if (audioContext) {
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      createGentleSound(audioContext);
    }
  }, [initAudioContext]);
  
  // Play default notification sound
  const playDefaultNotification = useCallback(() => {
    if (!isEnabledRef.current) return;
    
    try {
      const audio = new Audio(NOTIFICATION_SOUNDS.default);
      audio.volume = 0.5;
      audio.play().catch(e => {
        // Fall back to Web Audio API if autoplay blocked
        playGentleNotification();
      });
    } catch (e) {
      playGentleNotification();
    }
  }, [playGentleNotification]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  return {
    playUrgentAlert,
    playGentleNotification,
    playDefaultNotification,
    initAudioContext // Call this on user click to enable audio
  };
};

export default useSoundNotification;
